// Background warm-up for IT Dose report PDFs.
//
// Strategy:
//   When a list page (Workforce / Patient Reports) shows N visit cards,
//   we kick off a few GET requests to our backend's PDF endpoint in the
//   background — NOT to populate the browser cache, but to warm the
//   backend's in-memory PDF cache. By the time the user clicks a card,
//   the backend returns the PDF in <300ms instead of 3-6s.
//
//   We don't consume the response body on the client — the backend fills
//   its cache before res.send(), so even if the connection drops we win.
//
// Why the GLOBAL concurrency cap matters (this file's key invariant):
//   These warm requests hit IT Dose, which is SLOW (3-6s each). The PDF
//   endpoint is on the same origin as the page's own data API. A browser
//   only opens ~6 connections per origin (HTTP/1.1), so if warming isn't
//   capped globally it starves the real request: each page you open used
//   to spawn its own worker pool, and navigating a few pages piled up a
//   dozen+ slow warm requests that hogged every connection — the next
//   page's data fetch then hung forever on "loading".
//
//   So concurrency is capped GLOBALLY (MAX_INFLIGHT), not per call. No
//   matter how many pages you flip through, at most MAX_INFLIGHT warm
//   requests are ever in flight, leaving connections free for the page
//   itself. Warms are also sent with priority:'low' so the browser yields
//   sockets to higher-priority requests.

import { API_BASE_URL } from '../constants/api';

const TOKEN_KEY      = 'trustlab_token';
const PREFETCH_LIMIT = 6;   // top N visible cards to warm per page
const MAX_INFLIGHT    = 3;  // GLOBAL cap on concurrent warm requests

const seen   = new Set();   // already warmed (dedupe, ~10 min memory)
const queued = new Set();   // enqueued but not yet run
const queue  = [];          // pending { key, url }
let inflight = 0;

const drainBody = (res) => {
  // Release the connection cleanly without buffering the bytes we don't need.
  if (res.body && typeof res.body.cancel === 'function') {
    res.body.cancel().catch(() => {});
  } else if (typeof res.arrayBuffer === 'function') {
    res.arrayBuffer().catch(() => {});
  }
};

const runOne = async ({ key, url }) => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    // priority:'low' is a hint (Chrome) so the browser prefers the page's
    // own data fetch over these background warms. Ignored where unsupported.
    const res = await fetch(url, { method: 'GET', headers, priority: 'low' });
    seen.add(key);
    setTimeout(() => seen.delete(key), 10 * 60 * 1000);
    drainBody(res);
  } catch {
    // Best-effort — leave it un-seen so a later visit can retry.
  } finally {
    queued.delete(key);
  }
};

// Hoisted so the completion handler isn't a closure re-declared in the
// loop (and so pump/onOneDone can reference each other freely).
function onOneDone() {
  inflight -= 1;
  pump();
}

function pump() {
  while (inflight < MAX_INFLIGHT && queue.length > 0) {
    const item = queue.shift();
    inflight += 1;
    runOne(item).finally(onOneDone);
  }
}

const enqueue = (visitNo) => {
  if (!visitNo) return;
  const key = `v:${visitNo}`;
  if (seen.has(key) || queued.has(key)) return;
  queued.add(key);
  queue.push({
    key,
    url: `${API_BASE_URL}/portal/itdose-reports/pdf?visitId=${encodeURIComponent(visitNo)}`,
  });
};

// Warm the top few visits on a list page. Safe to call on every page /
// filter change — dedupe + the global cap keep it from piling up.
export const prefetchReportPdfs = (visits) => {
  if (!Array.isArray(visits) || visits.length === 0) return;
  visits.slice(0, PREFETCH_LIMIT).forEach((v) => enqueue(v?.visitNo));
  pump();
};

// Warm a single visit — hover / long-press / opening the detail modal.
export const prefetchVisit = (visitNo) => {
  enqueue(visitNo);
  pump();
};
